/**
 * heic-convert.js — window.HeicConvert: a pool of heic-convert-worker.js
 * workers behind one promise-returning API. Main-thread side only; the decode,
 * colour conversion, encode and Exif carry all happen in the workers, so a
 * 300-photo batch never blocks the page (the old path encoded every JPEG on
 * the main thread: putImageData + toBlob ≈ 150–300 ms of jank per photo).
 *
 *   const pool = HeicConvert.create({ workerUrl, heicToUrl, workers?, lanes? });
 *   const caps = await pool.ready;         // { webcodecs:'ok'|…, workers:N, lane:'webcodecs'|'wasm' }
 *   const { blob, meta } = await pool.convert(file, { format, quality, maxWidth, keepExif });
 *   const thumb = await pool.thumbnail(file, 200);   // Blob (jpeg)
 *   pool.stats()                            // { converted, byLane, avgMs }
 *   pool.destroy();
 *
 * Pool size. The first worker boots alone and reports the lane it can use;
 * the rest are sized for THAT lane:
 *   • WebCodecs: the GPU decoder is one device and the JPEG encode is CPU —
 *     min(3, cores) workers keep the encode parallel without stampeding the
 *     decoder;
 *   • libheif: each worker carries its own ~100 MB WASM instance, so the count
 *     follows memory: 1 at ≤4 GB, 2 at ≤8 GB, 3 above — measured 2026-09-05
 *     (16 photos): 1 instance 2.8 s/photo, 2 → 1.58, 3 → 1.23.
 * Jobs are handed to the first idle worker; each worker runs strictly serially.
 *
 * Failure semantics: a worker that dies (out of memory, script error) rejects
 * its in-flight job with code WORKER_DIED and is replaced once; if the pool
 * cannot boot at all, `ready` rejects and the caller uses its legacy path
 * (HeicDecode on the main thread).
 */
(function (w) {
    'use strict';
    if (w.HeicConvert) return;

    function create(cfg) {
        cfg = cfg || {};
        var workerUrl = cfg.workerUrl;
        if (!workerUrl) throw new Error('HeicConvert: workerUrl required');
        var heicToUrl = cfg.heicToUrl || '/vendor/heic-to/csp/heic-to.js';
        var lanes = cfg.lanes || null;
        var maxWorkers = cfg.workers | 0;
        var workers = [];
        var queue = [];
        var seq = 0;
        var destroyed = false;
        var caps = null;
        var stats = { converted: 0, byLane: {}, ms: 0, thumbs: 0, errors: 0, fallbacks: 0 };
        var respawns = 0;
        /* An engine without module workers (Firefox < 114, Safari < 15) starts this
         * worker as a classic script and it dies on its first import. Measured on an
         * emulated engine (08.10.2026, /heic-converter): every death spawned the next
         * worker — 574 workers and 573 page errors in 13 s, the shared «something went
         * wrong» toast renewed the whole time, while the photo was converted by the
         * legacy lane anyway. Asked first, the pool makes no worker and `ready` rejects
         * at once, which is the caller's cue for its legacy path. */
        var unsupported = null;
        if (w.__moduleWorkerOk && !w.__moduleWorkerOk()) {
            unsupported = new Error('module_worker_unsupported');
            unsupported.code = 'MODULE_WORKER_UNSUPPORTED';
        }

        function poolSizeFor(c) {
            if (maxWorkers > 0) return maxWorkers;
            var cores = (w.navigator && navigator.hardwareConcurrency) || 4;
            var mem = (w.navigator && navigator.deviceMemory) || 4;
            // GPU lane: each worker still holds two full-size RGBA canvases
            // (~100 MB at 12 MP) while it encodes, so a 4 GB device gets two.
            if (c && c.webcodecs === 'ok') return Math.max(1, Math.min(mem <= 4 ? 2 : 3, cores));
            if (mem <= 4) return 1;
            if (mem <= 8) return Math.min(2, Math.max(1, cores - 1));
            return Math.max(1, Math.min(3, cores - 1));
        }

        function spawn() {
            var wk;
            try { wk = new Worker(workerUrl, { type: 'module' }); }
            catch (e) { return Promise.reject(e); }
            var entry = { worker: wk, busy: null, pending: new Map(), ready: null, dead: false };
            entry.ready = new Promise(function (resolve, reject) {
                var id = 'init' + (++seq);
                entry.pending.set(id, { resolve: resolve, reject: reject, kind: 'init' });
                wk.onmessage = function (e) { onMessage(entry, e.data || {}); };
                wk.onerror = function (e) {
                    // The pool takes the failure (the job rejects, the caller falls back);
                    // uncancelled it would also reach window.onerror — the shared toast, Sentry.
                    if (e && e.preventDefault) e.preventDefault();
                    var err = new Error('worker error: ' + ((e && e.message) || 'unknown'));
                    err.code = 'WORKER_DIED';
                    onDeath(entry, err);
                    reject(err);
                };
                wk.onmessageerror = function () { onDeath(entry, Object.assign(new Error('worker message error'), { code: 'WORKER_DIED' })); };
                try { wk.postMessage({ type: 'init', id: id, heicToUrl: heicToUrl, lanes: lanes }); }
                catch (e) { reject(e); }
                setTimeout(function () {
                    if (entry.pending.has(id)) { entry.pending.delete(id); reject(Object.assign(new Error('worker init timeout'), { code: 'INIT_TIMEOUT' })); }
                }, 30000);
            });
            workers.push(entry);
            entry.ready.then(function () { entry.isReady = true; pump(); }, function () {});
            return entry.ready;
        }

        function onMessage(entry, m) {
            var p = entry.pending.get(m.id);
            if (!p) return;
            entry.pending.delete(m.id);
            if (p.kind !== 'init') { entry.busy = null; }
            if (m.type === 'error') {
                var err = new Error(m.message || 'conversion failed');
                err.code = m.code || 'CONVERT_FAILED';
                stats.errors++;
                p.reject(err);
            } else if (m.type === 'ready') {
                p.resolve(m.caps);
            } else if (m.type === 'done') {
                stats.converted++;
                stats.ms += (m.meta && m.meta.ms && m.meta.ms.total) || 0;
                var lane = (m.meta && m.meta.lane) || 'unknown';
                stats.byLane[lane] = (stats.byLane[lane] || 0) + 1;
                p.resolve({ blob: m.blob, meta: m.meta });
            } else if (m.type === 'thumb') {
                stats.thumbs++;
                p.resolve(m.blob);
            } else if (m.type === 'stats') {
                p.resolve(m);
            }
            pump();
        }

        function onDeath(entry, err) {
            if (entry.dead) return;
            entry.dead = true;
            entry.pending.forEach(function (p) { p.reject(err); });
            entry.pending.clear();
            entry.busy = null;
            var i = workers.indexOf(entry);
            if (i !== -1) workers.splice(i, 1);
            try { entry.worker.terminate(); } catch (e) {}
            // Replace a worker that crashed mid-life, so one crash does not shrink the
            // pool forever. Never one that died before it was ready — a pool that
            // cannot boot must not keep trying (each replacement was itself never
            // marked, so the old «replace once» respawned without end) — and never
            // more than a pool's worth over the pool's life.
            if (!destroyed && entry.isReady && respawns < poolSizeFor(caps) && workers.length < poolSizeFor(caps)) {
                respawns++;
                spawn().then(pump, function () {});
            }
            pump();
        }

        function pump() {
            if (destroyed) return;
            for (var i = 0; i < workers.length && queue.length; i++) {
                var e = workers[i];
                if (e.dead || e.busy || !e.isReady) continue;
                var job = queue.shift();
                e.busy = job.id;
                e.pending.set(job.id, job);
                try { e.worker.postMessage(job.msg, job.transfer || []); }
                catch (err) { e.pending.delete(job.id); e.busy = null; job.reject(err); }
            }
        }

        function enqueue(kind, msg, transfer) {
            return new Promise(function (resolve, reject) {
                if (unsupported) { reject(unsupported); return; }
                if (destroyed) { reject(new Error('pool destroyed')); return; }
                var id = kind + (++seq);
                msg.id = id;
                queue.push({ id: id, kind: kind, msg: msg, transfer: transfer, resolve: resolve, reject: reject });
                pump();
            });
        }

        var ready = unsupported ? Promise.reject(unsupported) : spawn().then(function (c) {
            caps = c || {};
            var n = poolSizeFor(caps);
            // The extra workers boot in the background: the first one can start now.
            for (var i = workers.length; i < n; i++) spawn().catch(function () {});
            return {
                webcodecs: caps.webcodecs, calibration: caps.calibration,
                lane: caps.webcodecs === 'ok' ? 'webcodecs' : 'wasm',
                workers: n, hardwareConcurrency: caps.hardwareConcurrency, deviceMemory: caps.deviceMemory,
            };
        });
        ready.catch(function () {});

        function bytesOf(file) {
            if (file instanceof ArrayBuffer) return Promise.resolve(file);
            if (file && typeof file.arrayBuffer === 'function') return file.arrayBuffer();
            return Promise.reject(new TypeError('HeicConvert: File/Blob/ArrayBuffer expected'));
        }

        return {
            ready: ready,
            /** @returns Promise<{blob, meta}> */
            convert: function (file, opts) {
                var name = (file && file.name) || 'image.heic';
                return ready.then(function () { return bytesOf(file); }).then(function (buf) {
                    return enqueue('c', { type: 'convert', buffer: buf, name: name, opts: opts || {} }, [buf]);
                });
            },
            /** @returns Promise<Blob> */
            thumbnail: function (file, maxSide) {
                return ready.then(function () { return bytesOf(file); }).then(function (buf) {
                    return enqueue('t', { type: 'thumb', buffer: buf, maxSide: maxSide || 200 }, [buf]);
                });
            },
            pending: function () { var busy = 0; workers.forEach(function (e) { if (e.busy) busy++; }); return queue.length + busy; },
            stats: function () {
                return { converted: stats.converted, byLane: stats.byLane, avgMs: stats.converted ? Math.round(stats.ms / stats.converted) : 0,
                    thumbs: stats.thumbs, errors: stats.errors, workers: workers.length, lane: caps && caps.webcodecs === 'ok' ? 'webcodecs' : 'wasm' };
            },
            caps: function () { return caps; },
            destroy: function () {
                destroyed = true;
                queue.splice(0).forEach(function (j) { j.reject(new Error('pool destroyed')); });
                workers.splice(0).forEach(function (e) { try { e.worker.postMessage({ type: 'close' }); } catch (x) {} try { e.worker.terminate(); } catch (x) {} });
            },
        };
    }

    w.HeicConvert = { create: create };
})(window);
